import React, { useEffect, useRef, useState } from 'react'
import { authService } from '@/services/authService'
import { useForceLightMode } from '@/contexts/ThemeContext'
import type { ApiError } from '@/services/api'

const OTP_LENGTH = 6
const DEFAULT_RESEND_COOLDOWN = 60

// sanitizeDigit keeps only a single digit (0-9), used for paste-proof input.
const sanitizeDigit = (value: string): string => {
  const d = value.replace(/\D/g, '')
  return d.length > 0 ? d[0] : ''
}

interface VerifyEmailPageProps {
  // Optional override of the authService for testing.
  service?: typeof authService
  initialCooldown?: number
}

export const VerifyEmailPage: React.FC<VerifyEmailPageProps> = ({
  service = authService,
  initialCooldown = DEFAULT_RESEND_COOLDOWN,
}) => {
  useForceLightMode()

  const [digits, setDigits] = useState<string[]>(Array(OTP_LENGTH).fill(''))
  const inputRefs = useRef<Array<HTMLInputElement | null>>([])
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle')
  const [error, setError] = useState<string | null>(null)
  const [resendCooldown, setResendCooldown] = useState<number>(initialCooldown)
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending'>('idle')

  // Tick cooldown every second.
  useEffect(() => {
    if (resendCooldown <= 0) return
    const t = window.setInterval(() => setResendCooldown((c) => (c <= 0 ? 0 : c - 1)), 1000)
    return () => window.clearInterval(t)
  }, [resendCooldown])

  // Focus first input on mount.
  useEffect(() => {
    inputRefs.current[0]?.focus()
  }, [])

  const handleDigitChange = (idx: number, raw: string) => {
    const cleaned = sanitizeDigit(raw)
    const next = [...digits]
    next[idx] = cleaned
    setDigits(next)
    setError(null)

    // Auto-advance.
    if (cleaned && idx < OTP_LENGTH - 1) {
      inputRefs.current[idx + 1]?.focus()
    }

    // Auto-submit when all filled.
    if (cleaned && idx === OTP_LENGTH - 1 && next.every((d) => d !== '')) {
      void submitCode(next.join(''))
    }
  }

  const handleKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH)
    if (!pasted) return
    e.preventDefault()
    const next = Array(OTP_LENGTH).fill('')
    for (let i = 0; i < pasted.length; i++) {
      next[i] = pasted[i]
    }
    setDigits(next)
    const focusIdx = Math.min(pasted.length, OTP_LENGTH - 1)
    inputRefs.current[focusIdx]?.focus()
    if (pasted.length === OTP_LENGTH) {
      void submitCode(pasted)
    }
  }

  const submitCode = async (code: string) => {
    if (code.length !== OTP_LENGTH) {
      setError(`Kode OTP harus ${OTP_LENGTH} digit`)
      return
    }
    setStatus('submitting')
    setError(null)
    try {
      const res = await service.verifyOtp(code, 'registration')
      if (!res.success) {
        throw new Error(res.error?.message || 'Verifikasi gagal')
      }

      // Fetch updated user to reflect verified status in local storage
      try {
        const meRes = await service.getCurrentUser()
        if (meRes.success && meRes.data) {
          localStorage.setItem('user', JSON.stringify(meRes.data))
        }
      } catch (e) {
        console.error('Failed to refresh user after verification', e)
      }

      setStatus('success')
      // Force full reload to update AuthContext state
      setTimeout(() => { window.location.href = '/dashboard' }, 500)
    } catch (err) {
      const e = err as ApiError
      const code = e?.code
      setStatus('idle')
      if (code === 'OTP_EXPIRED') {
        setError('Kode OTP sudah kedaluwarsa. Kirim ulang untuk mendapatkan kode baru.')
      } else if (code === 'OTP_MAX_RETRIES') {
        setError('Terlalu banyak percobaan. Kirim ulang OTP baru.')
      } else if (code === 'OTP_INVALID') {
        setError('Kode OTP salah. Coba lagi.')
      } else {
        setError(e?.message || 'Verifikasi gagal. Coba lagi.')
      }
    }
  }

  const handleResend = async () => {
    if (resendCooldown > 0 || resendStatus === 'sending') return
    setResendStatus('sending')
    setError(null)
    try {
      const res = await service.resendOtp('registration')
      if (!res.success) throw new Error(res.error?.message || 'Gagal mengirim ulang OTP')
      setResendCooldown(initialCooldown)
    } catch (err) {
      const e = err as ApiError
      setError(e?.message || 'Gagal mengirim ulang OTP')
    } finally {
      setResendStatus('idle')
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center pt-32 md:pt-40 bg-slate-50 dark:bg-slate-950 py-12 px-4 relative overflow-hidden" data-testid="verify-email-page">
      <div className="absolute top-[-10%] right-[-5%] w-[30%] h-[30%] bg-primary/5 rounded-full blur-[100px]" />
      <div className="max-w-md w-full bg-white/70 dark:bg-slate-900/40 backdrop-blur-xl p-10 rounded-[2rem] border border-white dark:border-white/5 shadow-2xl space-y-8 relative z-10">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Verifikasi Email</h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
            Masukkan kode {OTP_LENGTH} digit yang telah kami kirimkan ke email Anda.
          </p>
        </div>

        <div className="flex justify-between gap-2" role="group" aria-label="Kode OTP">
          {digits.map((d, idx) => (
            <input
              key={idx}
              ref={(el) => (inputRefs.current[idx] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              aria-label={`Digit ${idx + 1}`}
              value={d}
              onChange={(e) => handleDigitChange(idx, e.target.value)}
              onKeyDown={(e) => handleKeyDown(idx, e)}
              onPaste={handlePaste}
              disabled={status === 'submitting' || status === 'success'}
              className="w-12 h-12 text-center text-xl font-semibold border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              data-testid={`otp-digit-${idx}`}
            />
          ))}
        </div>

        {error && (
          <div role="alert" className="text-sm text-red-600 dark:text-red-400" data-testid="otp-error">
            {error}
          </div>
        )}

        {status === 'success' && (
          <div role="status" className="text-sm text-green-600" data-testid="otp-success">
            Email terverifikasi. Mengalihkan...
          </div>
        )}

        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={handleResend}
            disabled={resendCooldown > 0 || resendStatus === 'sending'}
            className="text-sm font-medium text-blue-600 hover:underline disabled:text-gray-400 disabled:cursor-not-allowed"
            data-testid="resend-otp-button"
          >
            {resendCooldown > 0
              ? `Kirim ulang dalam ${resendCooldown}s`
              : resendStatus === 'sending'
              ? 'Mengirim...'
              : 'Kirim ulang kode'}
          </button>

          <button
            type="button"
            onClick={() => void submitCode(digits.join(''))}
            disabled={digits.some((d) => d === '') || status === 'submitting'}
            className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50"
            data-testid="verify-otp-button"
          >
            {status === 'submitting' ? 'Memverifikasi...' : 'Verifikasi'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default VerifyEmailPage
